import { Eye } from 'lucide-react';

import { imageService } from '@repo/ui/lib/services/image.service';
import { MapMarkerData } from './types';

interface MapInfoWindowContentProps {
  readonly marker: MapMarkerData;
  readonly onViewDetails?: (inventaireId: string) => void;
  readonly onOpenStreetView?: (
    position: { lat: number; lng: number },
    title?: string,
  ) => void;
}

export const MapInfoWindowContent = ({
  marker,
  onViewDetails,
  onOpenStreetView,
}: MapInfoWindowContentProps) => {
  const arbre = marker.data?.arbre;
  const displayImageUrl = arbre?.imgUrl?.img1
    ? imageService.getOptimizedImageUrl(arbre.imgUrl.img1, 'arbre', 800, 600)
    : arbre?.espece?.img_url
      ? imageService.getPrimaryImage(arbre.espece.img_url, 'espece')
      : null;
  const canShowDetails = Boolean(onViewDetails && marker.data?.id);
  const canOpenStreetView = Boolean(
    onOpenStreetView && marker.position?.lat && marker.position?.lng,
  );

  return (
    <div className="">
      {displayImageUrl ? (
        <div className="w-full h-32 bg-gray-200">
          <img
            src={displayImageUrl}
            alt={marker.title || 'Arbre'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
          <span className="text-gray-400 text-sm">Aucune photo</span>
        </div>
      )}

      <div className="p-3 bg-white space-y-2">
        {arbre?.espece?.nom_fr && (
          <h3 className="font-bold text-lg text-gray-800">{arbre.espece.nom_fr}</h3>
        )}

        {arbre?.espece?.name && <p className="text-sm text-gray-700">{arbre.espece.name}</p>}

        <div className="space-y-1">
          {arbre?.hauteur && (
            <p className="text-sm text-gray-600">Hauteur : {arbre.hauteur}m</p>
          )}
          {arbre?.diametre && (
            <p className="text-sm text-gray-600">Diamètre : {arbre.diametre}cm</p>
          )}
        </div>

        {marker.data?.updated_at && (
          <p className="text-xs text-gray-500">
            Mise à jour le {new Date(marker.data.updated_at).toLocaleDateString('fr-FR')}
          </p>
        )}

        {(canShowDetails || canOpenStreetView) && (
          <div className="pt-2 border-t border-gray-200 space-y-2">
            {canShowDetails && (
              <button
                type="button"
                onClick={() => onViewDetails?.(marker.data!.id)}
                className="w-full bg-green-600 hover:bg-green-700 text-white text-xs py-2 px-3 rounded-md transition-colors duration-200"
              >
                Voir plus
              </button>
            )}
            {canOpenStreetView && (
              <button
                type="button"
                onClick={() =>
                  onOpenStreetView?.(
                    { lat: marker.position.lat, lng: marker.position.lng },
                    marker.title,
                  )
                }
                className="w-full inline-flex items-center justify-center gap-1.5 bg-white hover:bg-gray-50 text-gray-800 text-xs py-2 px-3 rounded-md border border-gray-300 transition-colors duration-200"
              >
                <Eye className="h-3.5 w-3.5" />
                <span>Voir Street View</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
